<script setup>
// The CSAT answers (R3) on the kit's table: contact (with the conversation and when), the rating as a kit badge (its
// name only, without the emoji: R3-15 = B, POLARIS_REPORTS.RATING_NAMES; the woot's CSAT.RATINGS, with the emoji,
// stays for the conversation's CSAT bubble and the survey), the comment ("show more" when long) and the agent. With
// review notes on the account, a row opens under itself the woot's notes editor (kept: the kit's card did not draw
// it), with a chevron button that says whether it is open. 25 per page with the kit's footer, same `pageChange` as
// before.
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';
import { ChevronDown16 } from '@carbon/icons-vue';

import { messageStamp, dynamicTime } from 'shared/helpers/timeHelper';

import ShowMore from 'dashboard/components/widgets/ShowMore.vue';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'dashboard/components-next/polaris/ui/table';
import { Badge } from 'dashboard/components-next/polaris/ui/badge';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import { Card } from 'dashboard/components-next/polaris/ui/card';
import { Skeleton } from 'dashboard/components-next/polaris/ui/skeleton';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from 'dashboard/components-next/polaris/ui/empty';
import UserAvatar from 'dashboard/components-next/polaris/cards/UserAvatar.vue';
import PaginationFooter from 'dashboard/components-next/polaris/cards/PaginationFooter.vue';
import CsatContactCell from './CsatContactCell.vue';
import CsatExpandedRow from './CsatExpandedRow.vue';

import { csatRatingNames } from './Csat/csatRatingNames';

const { pageIndex } = defineProps({
  pageIndex: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['pageChange']);
const { t } = useI18n();
const { isCloudFeatureEnabled, isOnChatwootCloud } = useAccount();
const csatResponses = useMapGetter('csat/getCSATResponses');

const isFeatureEnabled = computed(() =>
  isCloudFeatureEnabled('csat_review_notes')
);
const showExpandableRows = computed(
  () => isFeatureEnabled.value || isOnChatwootCloud.value
);
const metrics = useMapGetter('csat/getMetrics');
const uiFlags = useMapGetter('csat/getUIFlags');

const isLoading = computed(() => uiFlags.value.isFetching);

const PAGE_SIZE = 25;
const columnCount = computed(() => (showExpandableRows.value ? 5 : 4));

const expandedRows = ref({});

const toggleRow = id => {
  expandedRows.value = {
    ...expandedRows.value,
    [id]: !expandedRows.value[id],
  };
};

const isRowExpanded = id => !!expandedRows.value[id];

const tableData = computed(() => {
  return csatResponses.value.map(response => ({
    id: response.id,
    contact: response.contact,
    assignedAgent: response.assigned_agent,
    rating: response.rating,
    feedbackText: response.feedback_message || '',
    conversationId: response.conversation_id,
    createdAgo: dynamicTime(response.created_at),
    createdAt: messageStamp(response.created_at, 'LLL d yyyy, h:mm a'),
    _original: response,
  }));
});

// The rating's colour in the kit's words (the report only; CSAT_RATINGS.color stays for the widget).
const RATING_BADGES = {
  5: 'success',
  4: 'success',
  3: 'secondary',
  2: 'warning',
  1: 'destructive',
};
const ratingNames = computed(() => csatRatingNames(t));

const page = computed(() => pageIndex + 1);
const onPageChange = value => emit('pageChange', value - 1);
</script>

<template>
  <Card class="min-w-0 gap-0 overflow-hidden py-0" data-csat-table>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead class="px-4">
            {{ t('CSAT_REPORTS.TABLE.HEADER.CONTACT_NAME') }}
          </TableHead>
          <TableHead class="w-36 px-4">
            {{ t('CSAT_REPORTS.TABLE.HEADER.RATING') }}
          </TableHead>
          <TableHead class="px-4">
            {{ t('CSAT_REPORTS.TABLE.HEADER.FEEDBACK_TEXT') }}
          </TableHead>
          <TableHead class="w-48 px-4">
            {{ t('CSAT_REPORTS.TABLE.HEADER.HANDLED_BY') }}
          </TableHead>
          <TableHead v-if="showExpandableRows" class="w-12 px-4">
            <span class="sr-only">{{
              t('CSAT_REPORTS.REVIEW_NOTES.TITLE')
            }}</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <template v-if="isLoading">
          <TableRow v-for="n in 5" :key="n">
            <TableCell class="px-4"><Skeleton class="h-8 w-40" /></TableCell>
            <TableCell class="px-4"><Skeleton class="h-5 w-20" /></TableCell>
            <TableCell class="px-4"><Skeleton class="h-4 w-64" /></TableCell>
            <TableCell class="px-4"><Skeleton class="h-6 w-32" /></TableCell>
            <TableCell v-if="showExpandableRows" class="px-4" />
          </TableRow>
        </template>
        <TableRow v-else-if="!tableData.length" class="hover:bg-transparent">
          <TableCell :colspan="columnCount" class="p-0">
            <Empty class="border-0 md:p-8">
              <EmptyHeader>
                <EmptyTitle>{{ t('CSAT_REPORTS.NO_RECORDS') }}</EmptyTitle>
                <EmptyDescription>
                  {{ t('CSAT_REPORTS.NO_RECORDS_DESCRIPTION') }}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </TableCell>
        </TableRow>
        <template v-else>
          <template v-for="row in tableData" :key="row.id">
            <TableRow
              :data-state="isRowExpanded(row.id) ? 'selected' : undefined"
              :class="{ 'cursor-pointer': showExpandableRows }"
              @click="showExpandableRows && toggleRow(row.id)"
            >
              <TableCell class="px-4 align-top">
                <CsatContactCell
                  :contact="row.contact"
                  :conversation-id="row.conversationId"
                  :created-ago="row.createdAgo"
                  :created-at="row.createdAt"
                />
              </TableCell>
              <TableCell class="px-4 align-top">
                <Badge
                  :variant="RATING_BADGES[row.rating] || 'secondary'"
                  :data-rating="row.rating"
                >
                  {{ ratingNames[row.rating] }}
                </Badge>
              </TableCell>
              <TableCell
                class="min-w-64 max-w-md whitespace-normal break-words px-4 align-top"
              >
                <span
                  v-if="!row.feedbackText"
                  class="italic text-polaris-muted-foreground"
                >
                  {{ t('CSAT_REPORTS.NO_FEEDBACK') }}
                </span>
                <ShowMore v-else :text="row.feedbackText" :limit="100" />
              </TableCell>
              <TableCell class="max-w-48 px-4 align-top">
                <div
                  v-if="row.assignedAgent"
                  class="flex min-w-0 items-center gap-2"
                >
                  <UserAvatar
                    :name="row.assignedAgent.name"
                    :src="row.assignedAgent.thumbnail || ''"
                    :size="24"
                    decorative
                  />
                  <span class="truncate">{{ row.assignedAgent.name }}</span>
                </div>
                <span v-else class="italic text-polaris-muted-foreground">
                  {{ t('CSAT_REPORTS.NO_AGENT') }}
                </span>
              </TableCell>
              <TableCell v-if="showExpandableRows" class="px-4 align-top">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  :aria-expanded="isRowExpanded(row.id)"
                  :aria-label="t('CSAT_REPORTS.REVIEW_NOTES.TITLE')"
                  @click.stop="toggleRow(row.id)"
                >
                  <ChevronDown16
                    class="transition-transform"
                    :class="{ 'rotate-180': isRowExpanded(row.id) }"
                    aria-hidden="true"
                  />
                </Button>
              </TableCell>
            </TableRow>
            <TableRow
              v-if="showExpandableRows && isRowExpanded(row.id)"
              class="hover:bg-transparent"
            >
              <TableCell :colspan="columnCount" class="p-0 whitespace-normal">
                <CsatExpandedRow :response="row._original" />
              </TableCell>
            </TableRow>
          </template>
        </template>
      </TableBody>
    </Table>
    <div
      v-if="metrics.totalResponseCount"
      class="border-0 border-t border-solid border-polaris-border px-4 py-3"
    >
      <PaginationFooter
        :page="page"
        :total="metrics.totalResponseCount"
        :page-size="PAGE_SIZE"
        count-key="POLARIS_REPORTS.CSAT.COUNT"
        showing-key="POLARIS_REPORTS.CSAT.SHOWING"
        @update:page="onPageChange"
      />
    </div>
  </Card>
</template>
